import {
    CurrencyUnitTotalType,
    FareComponentInBookingType,
    FareFamilyType,
    NameValueType,
    OfferType,
    PriceDetailInBookingType,
    PriceEnvelopeInOfferType,
    PriceInOfferType,
    PriceItemType,
    ResponseForConvertOfferAfx2jsonType,
    ServiceInTripType,
    ServiceXRefType
} from '@afidium/ts-facade';
import { Injectable } from '@angular/core';
import { Yii } from 'libraries/interfaces/Yii';

export interface IFareFamilyTable {
    columns: IFareFamilyTableColumn[];
    rows: IFareFamilyTableRow[];
}

export interface IFareFamilyTableColumn {
    key: string;
    label: string;
    xId?: string;
    altenativeOfferXId?: string;
}

export interface IFareFamilyTableRow {
    [key: string]: {
        data: IFareFamilyTableRowData;
        type: ECellType;
    };
}

export interface IFareFamilyTableRowData {
    value: string | number;
    isSelectable: boolean;
    unit?: string;
    priceDifference?: string;
    currencyCode?: string;
    decimals?: number;
    quantity?: number | string;
}

export enum ECellType {
    Co2 = "co2",
    Rating = "rating",
    Service = "service",
    Price = "price",
    LineName = "lineName"
}

export const LINE_NAMES_COLUMN: string = "LINE_NAMES_COLUMN";

@Injectable()
export class FareFamilyTableService {

    public getDataSource(fareFamilyBenchmark: ResponseForConvertOfferAfx2jsonType, isRetail: boolean = false): IFareFamilyTable {
        if (fareFamilyBenchmark.dataList) {
            const fareFamilyArray: FareFamilyType[] = fareFamilyBenchmark.dataList.fareFamilyArray;
            const serviceArray: ServiceInTripType[] = fareFamilyBenchmark.dataList.serviceArray;
            const priceArray: PriceInOfferType[] = fareFamilyBenchmark.offer.priceArray || [];
            const alternativesPriceArray: PriceInOfferType[] = getAlternativesPriceArray(fareFamilyBenchmark.offer);
            const priceInOfferArray: PriceInOfferType[] = [...priceArray, ...alternativesPriceArray];

            const columns: IFareFamilyTableColumn[] = getColumns(fareFamilyArray, priceInOfferArray);
            const priceRow: IFareFamilyTableRow = getPriceRow(columns, fareFamilyBenchmark.offer, isRetail);
            const serviceRows: IFareFamilyTableRow[] = (serviceArray || [])
                .map((service: ServiceInTripType): IFareFamilyTableRow => getServiceRow(service, fareFamilyArray, columns));
            return {
                columns,
                rows: [
                    priceRow,
                    ...serviceRows
                ].filter((row: IFareFamilyTableRow): boolean => Boolean(row)),
            };
        }
        return null;
    }

    public getDisplayedColumns(fareFamilyBenchmark: ResponseForConvertOfferAfx2jsonType): string[] {
        if (fareFamilyBenchmark?.dataList?.fareFamilyArray) {
            return [
                LINE_NAMES_COLUMN,
                ...fareFamilyBenchmark.dataList.fareFamilyArray.map(({xId}: FareFamilyType): string => xId)
            ];
        }
        return [LINE_NAMES_COLUMN];
    }

    public getSelectableFareFamilies(displayedColumns: string[], fareFamilyBenchmark: ResponseForConvertOfferAfx2jsonType)
        : Map<string, boolean> {
        const fareFamilyColumns: string[] = displayedColumns.filter((columnName: string): boolean => columnName !== LINE_NAMES_COLUMN);
        const offerPriceArray: PriceInOfferType[] = fareFamilyBenchmark.offer?.priceArray || [];
        const alternativesPriceArray: PriceInOfferType[] = getAlternativesPriceArray(fareFamilyBenchmark.offer);
        const priceArray: PriceInOfferType[] = [
            ...offerPriceArray,
            ...alternativesPriceArray
        ];
        const selectableFareFamiliesMap: Map<string, boolean> = new Map<string, boolean>();
        fareFamilyColumns.forEach((fareFamilyXId: string): void => {
            const price: PriceEnvelopeInOfferType = getPriceInOfferFromFareFamilyXId(priceArray, fareFamilyXId, true);
            const fareFamiliesInPrice: Set<string> = new Set<string>();
            if (price) {
                price.detailArray
                    .filter((detailArray: PriceDetailInBookingType): boolean => Boolean(detailArray.rate)
                        && Boolean(detailArray.rate?.fareComponentArray))
                    .forEach((detailArray: PriceDetailInBookingType): void => {
                        detailArray.rate.fareComponentArray
                            .forEach((fareComponent: FareComponentInBookingType): void => {
                                if (fareComponent) {
                                    fareFamiliesInPrice.add(fareComponent.fareFamily?.xRef || 'none');
                                }
                            });
                    });
                selectableFareFamiliesMap.set(fareFamilyXId, fareFamiliesInPrice.size === 1);
            }

        });
        return selectableFareFamiliesMap;
    }
}

function getColumns(fareFamilyArray: FareFamilyType[], priceInOfferArray: PriceInOfferType[]): IFareFamilyTableColumn[] {

    return [
        {key: LINE_NAMES_COLUMN, label: Yii.t('wui-iqba-js', "Fare Family")},
        ...fareFamilyArray.map(({name, xId, code}: FareFamilyType): IFareFamilyTableColumn => ({
            key: xId,
            label: name?.value || code,
            xId,
            altenativeOfferXId: getAternativeOfferXId(xId, priceInOfferArray)
        }))
    ];
}

function getAternativeOfferXId(xId: string, priceInOfferArray: PriceInOfferType[]): string {
    const price: PriceEnvelopeInOfferType = getPriceInOfferFromFareFamilyXId(priceInOfferArray, xId, true);
    if (!price) {
        return null;
    }
    return price.xId;
}


function getAlternativesPriceArray(offer: OfferType): PriceInOfferType[] {
    if (!offer?.offerAlternatives?.offerArray?.length) {
        return [];
    }
    return offer.offerAlternatives.offerArray
        .reduce((acc: PriceInOfferType[], offerItem: OfferType): PriceInOfferType[] => [
            ...acc,
            ...(offerItem.priceArray || [])
        ], []);
}

function getPriceRow(columns: IFareFamilyTableColumn[], offer: OfferType, isRetail: boolean = false): IFareFamilyTableRow {
    return columns.reduce((acc: IFareFamilyTableRow, column: IFareFamilyTableColumn): IFareFamilyTableRow => {
        if (column.key === LINE_NAMES_COLUMN) {
            return {
                ...acc,
                [column.key]: {
                    type: ECellType.LineName,
                    data: {
                        value: Yii.t('wui-iqba-js', isRetail ? 'Total Vente' : 'Total Achat'),
                        isSelectable: false
                    }
                }
            };
        }
        let relatedOffer: OfferType;

        if (!offer.offerAlternatives) {
            relatedOffer = offer;
        } else {
            const isOffer: boolean = Boolean(getPriceArrayInOfferFromFareFamilyXId(offer.priceArray, column.xId, isRetail)?.length);

            if (isOffer) {
                relatedOffer = offer;
            } else {
                relatedOffer = offer.offerAlternatives.offerArray.find((offerItem: OfferType): boolean => {
                    return Boolean(getPriceArrayInOfferFromFareFamilyXId(offerItem.priceArray, column.xId, isRetail)?.length);
                });
            }
        }
        if (!relatedOffer) {
            return null;
        }

        const relatedOfferTotalArray: PriceItemType[] = isRetail && relatedOffer.total?.retail?.totalArray ? relatedOffer.total.retail.totalArray : relatedOffer.total.cost.totalArray;
        // console.log({xId: column.xId, relatedOffer: _cloneDeep(relatedOffer)});
        const relatedOfferTotal: CurrencyUnitTotalType[] = relatedOfferTotalArray
            .find((totalItem: PriceItemType): boolean => !totalItem.xReason)
            .currencyArray;

        const offerTotalValue: number = relatedOfferTotal.reduce((accTotal: number, item: CurrencyUnitTotalType): number => {
            return accTotal + item.value;
        }, 0);
        const offerTotalCurrencyCode: string = relatedOfferTotal[0].currencyCode;
        const offerTotalDecimals: number = relatedOfferTotal[0].decimals;
        return {
            ...acc,
            [column.key]: {
                type: ECellType.Price,
                data: {
                    value: offerTotalValue,
                    isSelectable: false,
                    currencyCode: offerTotalCurrencyCode,
                    decimals: offerTotalDecimals,
                }
            }
        };
    }, {});
}

function getPriceInOfferFromFareFamilyXId(priceArray: PriceInOfferType[], xId: string, isRetail: boolean = false): PriceEnvelopeInOfferType {
    return (priceArray || [])
        .filter((price: PriceInOfferType): PriceEnvelopeInOfferType => isRetail && price.retail ? price.retail : price.cost)
        .map((price: PriceInOfferType): PriceEnvelopeInOfferType => isRetail && price.retail ? price.retail : price.cost)
        .find(({detailArray}: PriceEnvelopeInOfferType): boolean =>
            (detailArray || []).some(({rate}: PriceDetailInBookingType): boolean =>
                (rate?.fareComponentArray || []).some(({fareFamily}: FareComponentInBookingType): boolean =>
                    fareFamily?.xRef === xId)
            )
        );
}

function getPriceArrayInOfferFromFareFamilyXId(priceArray: PriceInOfferType[], xId: string, isRetail: boolean = false): PriceEnvelopeInOfferType[] {
    return (priceArray || [])
        .filter((price: PriceInOfferType): PriceEnvelopeInOfferType => isRetail && price.retail ? price.retail : price.cost)
        .map((price: PriceInOfferType): PriceEnvelopeInOfferType => isRetail && price.retail ? price.retail : price.cost)
        .filter(({detailArray}: PriceEnvelopeInOfferType): boolean => {
                // const allFareComponentAreRelated: boolean = (detailArray || []).some(({rate}: PriceDetailInBookingType): boolean => {
                //     console.log(rate?.fareComponentArray.map((fc)=> fc.fareFamily.xRef).join(' * '))
                //         return (rate?.fareComponentArray || [])
                //             .every(({fareFamily}: FareComponentInBookingType): boolean => fareFamily?.xRef === xId);
                //     }
                // );
                // if (allFareComponentAreRelated) {
                //     return allFareComponentAreRelated;
                // }
                return (detailArray || []).some(({rate}: PriceDetailInBookingType): boolean => {
                        return (rate?.fareComponentArray || [])
                            .some(({fareFamily}: FareComponentInBookingType): boolean => fareFamily?.xRef === xId);
                    }
                );
            }
        );
}

function getServiceRow(service: ServiceInTripType, fareFamilyArray: FareFamilyType[], columns: IFareFamilyTableColumn[]): IFareFamilyTableRow {
    return columns.reduce((acc: IFareFamilyTableRow, column: IFareFamilyTableColumn): IFareFamilyTableRow => {
        if (column.key === LINE_NAMES_COLUMN) {
            return {
                ...acc,
                [column.key]: {
                    type: ECellType.LineName,
                    data: {
                        value: service.nameArray?.length ?
                            service.nameArray.find(({value}: NameValueType): boolean => Boolean(value)).value :
                            service.code,
                        isSelectable: false
                    }
                }
            };
        }
        const serviceInFareFamilyArray: ServiceXRefType = fareFamilyArray
            .find(({xId}: FareFamilyType): boolean => xId === column.xId)
            .serviceArray?.find(({xRef}: ServiceXRefType): boolean => xRef === service.xId);
        const value: string = !serviceInFareFamilyArray ? null : serviceInFareFamilyArray.xApply;
        const quantity: string = service.xCategory === "Luggage" ? "x1" : null;
        return {
            ...acc,
            [column.key]: {
                type: ECellType.Service,
                data: {
                    value,
                    quantity,
                    isSelectable: false
                }
            }
        };
    }, {});
}
